import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType } from 'keycloak-angular';
import { MessageService } from '@openng/optimus-ui/api';
import { Optimus } from '@openng/optimus-ui/config';
import { AuthenticationService, AuthorizationService } from '~ngx-shared/authentication';
import { GraphQlService } from '~ngx-shared/graph-ql';
import { LayoutConfig, LayoutService, LoadingService, MenuService } from '~ngx-shared/layout';
import { FileService, VersionService } from '~ngx-shared/services';
import { environment } from '~shared/environment';
import { UserService } from '~shared/services';
import { AuthorizationUtil } from '~shared/utils';
import { Karima } from './theme/karima';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly layoutService = inject(LayoutService);
  private readonly translocoService = inject(TranslocoService);
  private readonly loadingService = inject(LoadingService);
  private readonly menuService = inject(MenuService);
  private readonly versionService = inject(VersionService);
  private readonly messageService = inject(MessageService);
  private readonly optimusConfig = inject(Optimus);
  private readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly graphQlService = inject(GraphQlService);

  constructor() {
    const authenticationService = inject(AuthenticationService);
    const userService = inject(UserService);
    const fileService = inject(FileService);

    fileService.init(
      `${environment.externalServiceUrl}/file/upload`,
      `${environment.externalServiceUrl}/file`
    );

    effect(() => {
      const isNewAvailable = this.versionService.isNewVersionAvailable();
      if (isNewAvailable) {
        this.messageService.add({
          severity: 'error',
          summary: 'update',
          closable: false,
          sticky: true,
          key: 'new_version_available'
        });
      }
    });

    effect(() => {
      const keycloakEvent = this.keycloakSignal();
      if (keycloakEvent.type === KeycloakEventType.Ready) {
        authenticationService.init(
          ['default-roles-iggoe', 'iggoe-user'],
          url =>
            url.startsWith(environment.hasuraUrl) || url.startsWith(environment.externalServiceUrl)
        );
        this.authorizationService.setAllowedRoles(
          Object.values(AuthorizationUtil.GROUP_ACCOUNTANT)
        );
        userService.init();

        this.menuService.sideBarItems$.next([
          {
            label: 'Home',
            items: [
              {
                label: 'dashboard',
                icon: 'pi pi-fw pi-home',
                routerLink: ['/', 'dashboard']
              },
              {
                label: 'profile',
                icon: 'pi pi-fw pi-user',
                routerLink: ['/', 'profile']
              }
            ]
          },
          {
            label: 'shared.finance',
            items: [
              {
                label: 'finance.account_plan',
                routerLink: ['/', 'account-plan'],
                icon: 'pi pi-fw pi-book'
              },
              {
                label: 'finance.entries',
                routerLink: ['/', 'entries'],
                icon: 'pi pi-fw pi-arrows-h'
              },
              {
                label: 'finance.camt_import',
                routerLink: ['/', 'camt-import'],
                icon: 'pi pi-fw pi-file-import'
              },
              {
                label: 'finance.accounting_period',
                routerLink: ['/', 'period'],
                icon: 'pi pi-fw pi-cog'
              },
              {
                label: 'finance.accounting_periods',
                routerLink: ['/', 'periods'],
                icon: 'pi pi-fw pi-calendar'
              }
            ]
          }
          // {
          //   label: 'shared.actors',
          //   items: [
          //     {
          //       label: 'shared.role.accountants',
          //       icon: 'pi pi-fw pi-briefcase',
          //       routerLink: ['/', 'actors', 'accountants'],
          //       state: { permissions: Role.MODERATOR }
          //     },
          //     {
          //       label: 'shared.people',
          //       icon: 'pi pi-fw pi-users',
          //       routerLink: ['/', 'actors', 'people']
          //     }
          //   ]
          // }
        ]);
      }
    });
  }

  ngOnInit(): void {
    this.loadingService.startLoading();

    //optional configuration with the default configuration
    const config: LayoutConfig = {
      preset: 'Aura',
      primary: 'teal',
      surface: null,
      darkTheme: false,
      menuMode: 'static'
    };
    this.layoutService.layoutConfig.set(config);
    this.optimusConfig.theme.set({
      ...this.optimusConfig.theme(),
      preset: Karima
    });

    this.translocoService.selectTranslateObject('primeng').subscribe(translated => {
      this.optimusConfig.setTranslation(translated);
    });

    this.authorizationService.currentRole$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(role => {
        if (role) {
          this.graphQlService.init(() => role);
        }
      });

    this.loadingService.stopLoading();
  }
}
